// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI, agentsAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

async function enrichAgentUser(userData) {
  if (userData?.userType !== 'agent' || !userData?.id) return userData;
  // Use treatment from login response if backend sent it (auth.service adds it for agents)
  if (userData.treatmentCategory === 'TREATMENT_1' || userData.treatmentCategory === 'TREATMENT_2') {
    return { ...userData, treatmentCategory: userData.treatmentCategory };
  }
  try {
    const agent = await agentsAPI.getById(userData.id);
    const treatmentCategory = agent?.treatmentCategory || 'TREATMENT_1';
    return { ...userData, treatmentCategory };
  } catch {
    return { ...userData, treatmentCategory: 'TREATMENT_1' };
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('accessToken');
    const loginTimestampRaw = localStorage.getItem('loginTimestamp');
    const now = Date.now();
    const MAX_AGE_MS = 24 * 60 * 60 * 1000;

    if (storedUser && token && loginTimestampRaw) {
      const loginTimestamp = Number(loginTimestampRaw) || 0;
      const age = now - loginTimestamp;
      if (loginTimestamp && age > MAX_AGE_MS) {
        // Session older than 24h – clear everything and force re-login
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('loginTimestamp');
      }
    }

    const freshStoredUser = localStorage.getItem('user');
    const freshToken = localStorage.getItem('accessToken');
    if (freshStoredUser && freshToken) {
      try {
        const parsed = JSON.parse(freshStoredUser);
        const normalized = parsed?.id ? parsed : parsed?.userId ? { ...parsed, id: parsed.userId } : parsed;
        setUser(normalized);
        setIsAuthenticated(true);
        // Always refresh agent treatment on load so admin changes (e.g. to Treatment 2) apply without re-login
        if (normalized?.userType === 'agent' && normalized?.id) {
          enrichAgentUser(normalized).then((enriched) => {
            setUser(enriched);
            localStorage.setItem('user', JSON.stringify(enriched));
          });
        }
      } catch {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('loginTimestamp');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { accessToken, refreshToken, user: userData } = response;
      // Set token before enriching so agentsAPI.getById (for treatmentCategory) is authenticated
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('loginTimestamp', String(Date.now()));
      const enriched = await enrichAgentUser(userData);
      localStorage.setItem('user', JSON.stringify(enriched));
      setUser(enriched);
      setIsAuthenticated(true);
      return { success: true, user: enriched };
    } catch (error) {
      const msg = error.response?.data?.message ?? error.response?.data?.error;
      const text = Array.isArray(msg) ? msg[0] : msg;
      return { success: false, error: text || 'Login failed' };
    }
  };

  const requestOtp = async (phone) => {
    try {
      const response = await authAPI.requestOtp(phone);
      if (response.deliverViaFrontend && response.notifierUrl && response.notifierPayload) {
        try {
          const notifierRes = await fetch(response.notifierUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(response.notifierPayload),
          });
          if (!notifierRes.ok) {
            console.warn('[Auth] Notifier responded', notifierRes.status, await notifierRes.text());
          }
        } catch (e) {
          console.warn('[Auth] Failed to POST OTP to notifier from browser:', e);
          return { success: false, error: 'Could not deliver OTP. Check that the notifier allows this origin (CORS) or try again.' };
        }
      }
      return { success: true, ...response };
    } catch (error) {
      // No response = network/connection issue (timeout, refused, CORS, wrong URL)
      const isNetworkError =
        !error.response &&
        (error.code === 'ECONNABORTED' ||
          error.code === 'ERR_NETWORK' ||
          error.message?.includes('Network') ||
          error.message?.includes('timeout'));
      const message = isNetworkError
        ? 'Could not reach server. Check your connection and try again.'
        : (error.response?.data?.message || 'Failed to request OTP');
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Auth] requestOtp failed:', error.message, error.code, error.response?.status);
      }
      return { success: false, error: message };
    }
  };

  const performLogout = async () => {
    try {
      await authAPI.logout();
    } catch {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setShowLogoutModal(false);
  };

  const logout = () => setShowLogoutModal(true);

  // Auto-logout after 24 hours from login time
  useEffect(() => {
    if (!user) return;
    const loginTimestampRaw = localStorage.getItem('loginTimestamp');
    if (!loginTimestampRaw) return;
    const loginTimestamp = Number(loginTimestampRaw) || 0;
    if (!loginTimestamp) return;
    const MAX_AGE_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const remaining = MAX_AGE_MS - (now - loginTimestamp);
    if (remaining <= 0) {
      // Already expired – clear immediately
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTimestamp');
      setUser(null);
      setIsAuthenticated(false);
      setShowLogoutModal(false);
      return;
    }
    const timer = setTimeout(() => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTimestamp');
      setUser(null);
      setIsAuthenticated(false);
      setShowLogoutModal(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [user]);

  const userRef = useRef(user);
  userRef.current = user;
  const refreshAgentTreatment = useCallback(async () => {
    const u = userRef.current;
    if (u?.userType !== 'agent' || !u?.id) return u;
    try {
      const enriched = await enrichAgentUser(u);
      setUser(enriched);
      localStorage.setItem('user', JSON.stringify(enriched));
      return enriched;
    } catch {
      return u;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated, loading, login, logout, requestOtp, refreshAgentTreatment,
      isAdmin: user?.userType === 'admin',
      isSupervisor: user?.userType === 'supervisor',
      isAgent: user?.userType === 'agent',
      isExternalUser: user?.userType === 'external_user',
      isTreatment2: user?.treatmentCategory === 'TREATMENT_2',
    }}>
      {children}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="logout-modal-title">
          <div className="bg-[#001E3C] rounded-2xl p-6 w-full max-w-sm border border-white/10 shadow-xl">
            <h3 id="logout-modal-title" className="text-xl font-black text-white mb-4">Log out</h3>
            <p className="text-white/80 mb-6">Are you sure you want to log out?</p>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-3 rounded-xl glass-card text-white font-bold">
                Cancel
              </button>
              <button
                type="button"
                onClick={performLogout}
                className="flex-1 py-3 rounded-xl btn-gold font-black uppercase">
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
